import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://assets.lolcdn.xyz/logo-2.png";

// Redesigned per design.md (14islands-derived monochrome editorial
// system). Full-bleed white bar, no background fill/border/shadow ever -
// not even on scroll, per spec ("No background fill, no border, no
// shadow"). Desktop (md+) uses a three-zone CSS grid (1fr / auto / 1fr)
// so the center nav links stay genuinely centered regardless of how wide
// the logo or CTA zone are. Mobile uses plain flex + justify-between
// instead of the same grid - fixed after the grid's empty middle column
// (nav links are display:none below md) wasn't reliably collapsing to
// zero width, leaving the hamburger button short of the true right edge.
// Flexbox's handling of a hidden middle child is simpler and more
// consistently reliable than a CSS Grid track with zero participating
// items - not worth trusting the same grid-based centering trick on a
// breakpoint where there's no center content to center in the first
// place. No filled button anywhere - "the link IS the button" per spec;
// Contact functions as a soft CTA through position (right zone) rather
// than styling.
//
// The real logo image is used here, not a text wordmark - an earlier
// pass in this redesign swapped it for typography to match the design
// spec's decoration-free nav pattern, but the actual brand asset already
// existed and should be the thing people see, not a generic substitute.

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'About', href: '/#about' },
    { name: 'Services', href: '/#services' },
    { name: 'Portfolio', href: '/portfolio', isRoute: true },
    { name: 'Maintenance', href: '/maintenance', isRoute: true },
    { name: 'Instant Quote', href: '/instant-quote', isRoute: true },
  ];

  return (
    <>
      <nav
        className="fixed top-0 left-0 w-full z-50 bg-paper"
        data-testid="navigation"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between md:grid md:grid-cols-[1fr_auto_1fr] py-6 md:py-8">
            {/* Left zone: logo */}
            <Link
              to="/"
              className="justify-self-start flex items-center"
              data-testid="logo-link"
            >
              <img
                src={LOGO_URL}
                alt="LOLCorp - Custom Software Development Company, London UK"
                width="140"
                height="50"
                fetchPriority="high"
                className="h-9 md:h-10 w-auto"
              />
            </Link>

            {/* Center zone: nav links */}
            <div className="hidden md:flex items-center gap-10 justify-self-center">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="font-bentonsans text-body text-ink hover:text-stone transition-colors duration-300"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="font-bentonsans text-body text-ink hover:text-stone transition-colors duration-300"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>

            {/* Right zone: Contact (soft CTA via position, not styling) */}
            <div className="justify-self-end flex items-center gap-6">
              <Link
                to="/consultation"
                className="hidden md:inline font-bentonsans text-body text-ink hover:text-stone transition-colors duration-300"
                data-testid="nav-book-consultation"
              >
                Contact
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-ink"
                data-testid="mobile-menu-button"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-paper z-40 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="font-aftenscreen text-heading text-ink"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="font-aftenscreen text-heading text-ink"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <Link
                to="/consultation"
                className="font-bentonsans text-body text-stone mt-4"
                data-testid="mobile-nav-book-consultation"
              >
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
